import { useState, useRef, useMemo } from 'react';
import { isSentinelModelId, type AxonEvent } from '@reflex/shared';
import type { RuntimeSwitch } from './active-model.js';
import type {
  TurnBlock,
  ChatMessage,
  ChatItem,
  ChatTokenUsage,
  CodexInitExtensions,
  PlanEntry,
} from './types.js';
import { durationSecs } from './parsers.js';
import { initWorkingDir } from './working-dir.js';

export interface PendingResult {
  cost: number;
  numTurns: number;
  durationMs: number;
  stopReason?: string;
}

/**
 * Running native-Codex token counters. `total` mirrors the thread-cumulative
 * `total` breakdown of the newest `thread/tokenUsage/updated` frame;
 * `turnBaseline` is the cumulative snapshot at the previous turn boundary, so
 * a finished turn's spend is `total − turnBaseline`.
 */
export interface CodexTokenUsageState {
  total: ChatTokenUsage;
  turnBaseline: ChatTokenUsage | null;
}

/**
 * Native-Codex session-init pairing state. Codex announces session metadata
 * only in the `thread/start` JSON-RPC *response*, so the handler records the
 * outbound request id, parses the paired response into `pending`, and emits a
 * `system_init` block at the next `turn/started`. `emittedSessionIds` dedupes
 * across reconnect replays — the duplicate handshake frames on resumed
 * streams are permanent (see docs/internal/plans/codex-session-info-card.md).
 */
export interface CodexSessionInitState {
  requestMethods: Map<string, string>;
  pending: { extensions: CodexInitExtensions; model: string | null } | null;
  emittedSessionIds: Set<string>;
}

export interface CodexSubagentState {
  taskId: string;
  agentPath: string;
  toolUses: number;
}

export interface BlockManager {
  currentTurnBlocks: TurnBlock[];
  /**
   * Directory the session runs in *now*, as announced by the newest
   * `system_init` block. Null until one arrives, and for protocols that
   * announce none.
   *
   * This is the transcript-wide fallback for surfaces that aren't a block.
   * Blocks themselves carry the directory they were pushed under, because a
   * row's paths are relative to where the agent was then, not where it is now
   * (see `BlockWorkingDir` in {@link ./types.ts} and {@link ./working-dir.ts}).
   */
  workingDir: string | null;
  /**
   * Model the stream last reported as answering — a session-init block's
   * model, or a mid-session switch a harness announced. Null until a session
   * announces one, so callers can fall back to the launch-time model. See
   * {@link ./active-model.ts}.
   */
  activeModel: string | null;
  /**
   * Record the model a protocol frame reports as now in effect. Every reader
   * funnels through here, so this is where "a model id fit to show" is
   * enforced: a CLI placeholder such as `<synthetic>` names no model and is
   * dropped rather than becoming a label.
   */
  noteModel: (model: string | null) => void;
  /**
   * Whether a switch announced for the post at `sequence` is newer than every
   * switch noted so far. Racing switches announce out of post order and the
   * record kept the later post, so an older announcement is stale. One
   * without a sequence is always taken.
   */
  acceptsSwitch: (sequence: number | null) => boolean;
  /**
   * Note a runtime switch: its model through {@link noteModel}, its sequence
   * as the new watermark. A stale one changes nothing and returns false.
   */
  noteSwitch: (change: RuntimeSwitch) => boolean;
  pushBlock: (block: TurnBlock) => void;
  updateBlocks: (updater: (blocks: TurnBlock[]) => TurnBlock[]) => void;
  lastBlock: () => TurnBlock | undefined;
  finalizeThinking: (eventTimestamp: number) => void;
  flushToMessage: (
    extra: Partial<Omit<ChatMessage, 'id' | 'role' | 'content' | 'blocks'>> | undefined,
    eventTimestamp: number,
  ) => ChatItem | null;
  /**
   * Empty the live block buffer without forgetting what the session
   * announced. For the caller that has already taken the pending blocks and
   * only needs them to stop rendering as a live turn — a full {@link reset}
   * there would drop the working directory and active model mid-session.
   */
  clearLiveBlocks: () => void;
  /** Forget the whole session: buffered blocks and everything derived from it. */
  reset: () => void;
  /**
   * Fill in session facts that older history announced and the loaded
   * window never saw. Only what is still unknown is taken: anything the
   * window already announced is newer, and newer wins.
   */
  seedSessionFacts: (facts: {
    workingDir: string | null;
    activeModel: string | null;
    /** The prefix's watermark; raises the window's, never lowers it. */
    switchSequence?: number | null;
  }) => void;
  /**
   * The reactive fields as of this call — the value in effect right now,
   * readable mid-fold before React has re-rendered with it — plus the
   * switch watermark, which only the fold reads.
   */
  snapshot: () => Pick<BlockManager, 'currentTurnBlocks' | 'workingDir' | 'activeModel'> & {
    switchSequence: number | null;
  };
  blocksRef: React.RefObject<TurnBlock[]>;
  thinkingStartRef: React.MutableRefObject<number | null>;
  /** Accumulated plan entries that persist across flush boundaries. */
  planEntriesRef: React.MutableRefObject<PlanEntry[]>;
  /** Tracks accumulated input JSON for a TodoWrite tool during streaming. null = not accumulating. */
  streamingTodoInputRef: React.MutableRefObject<string | null>;
  /** Accumulated result metadata from turns with pending background tasks. */
  pendingResultRef: React.MutableRefObject<PendingResult | null>;
  /** Latest native-Codex cumulative token usage plus the last turn baseline. */
  codexTokenUsageRef: React.MutableRefObject<CodexTokenUsageState | null>;
  /**
   * Per native-Codex item id, the streamed text already flushed out of the
   * live buffer by a mid-turn flush (steer, slash command). Codex terminal
   * `item/completed` frames carry the item's full text, so the renderer
   * needs this prefix to append only the not-yet-shown remainder instead of
   * duplicating what a flushed message already displays.
   */
  codexFlushedItemTextRef: React.MutableRefObject<Map<string, string>>;
  /** Native-Codex session-init request pairing and dedupe state. */
  codexSessionInitRef: React.MutableRefObject<CodexSessionInitState>;
  /** Codex child thread id to the task card representing that background agent. */
  codexSubagentsRef: React.MutableRefObject<Map<string, CodexSubagentState>>;
  /** Number of child-thread system terminals already handled by native Codex frames. */
  codexSubagentTerminalsRef: React.MutableRefObject<number>;
  /** ACP session ids that have already emitted a `system_init` block, for cross-flush dedup. */
  acpEmittedSessionIds: React.MutableRefObject<Set<string>>;
  /** Returns true if any tool_call block has an in-progress background task. */
  hasPendingBackgroundTasks: () => boolean;
}

let nextAssistantId = 0;

/**
 * Finalized assistant rows must keep their React identity when older history
 * is inserted ahead of them. A middle backfill rebuilds the stream fold from
 * event zero; with a process-global counter as the key, every
 * already-rendered turn would get a new one, React would remount the whole
 * loaded transcript, and `useMeasuredRowSizes` would synchronously lay it
 * all out again.
 *
 * The event that closes the row is the strongest anchor. A few flush paths
 * (notably a user prompt flushing leftover blocks) have no explicit closing
 * event, so fall back to the newest block event. Synthetic/story blocks have
 * neither and retain the old counter fallback.
 */
function finalizedAssistantId(
  blocks: readonly TurnBlock[],
  sourceEvent: AxonEvent | undefined,
): string {
  let anchor = sourceEvent;
  for (let i = blocks.length - 1; !anchor && i >= 0; i--) {
    anchor = blocks[i].sourceEvent;
  }
  return anchor ? `assistant-${anchor.id}` : `assistant-${++nextAssistantId}`;
}

/**
 * What a block manager reports outward as it folds: the live buffer and the
 * session facts it derives. The hook mirrors these into React state; a
 * detached manager (folding a history prefix off the render path) reads
 * them back through {@link BlockManager.snapshot}.
 */
export interface BlockManagerListeners {
  onBlocks?: (blocks: TurnBlock[]) => void;
  onWorkingDir?: (workingDir: string | null) => void;
  onActiveModel?: (activeModel: string | null) => void;
}

/**
 * A {@link BlockManager} whose reactive fields read synchronously from the
 * manager's own refs — the value in effect right now, not React state.
 */
export type BlockManagerCore = BlockManager;

/**
 * The block manager without React: every piece of state is a plain ref and
 * every method is a closure over them. {@link useBlockManager} wraps one of
 * these so the rendered transcript re-renders as the buffer and session
 * facts change; the paged-history prefix fold builds a throwaway one so it
 * can fold older turns with the exact machinery the live fold uses.
 */
export function createBlockManager(listeners: BlockManagerListeners = {}): BlockManagerCore {
  const workingDirRef: React.MutableRefObject<string | null> = { current: null };
  const activeModelRef: React.MutableRefObject<string | null> = { current: null };
  const switchSequenceRef: React.MutableRefObject<number | null> = { current: null };
  const blocksRef: React.MutableRefObject<TurnBlock[]> = { current: [] };
  const thinkingStartRef: React.MutableRefObject<number | null> = { current: null };
  const planEntriesRef: React.MutableRefObject<PlanEntry[]> = { current: [] };
  const streamingTodoInputRef: React.MutableRefObject<string | null> = { current: null };
  const pendingResultRef: React.MutableRefObject<PendingResult | null> = { current: null };
  const codexTokenUsageRef: React.MutableRefObject<CodexTokenUsageState | null> = {
    current: null,
  };
  const codexFlushedItemTextRef: React.MutableRefObject<Map<string, string>> = {
    current: new Map(),
  };
  const codexSessionInitRef: React.MutableRefObject<CodexSessionInitState> = {
    current: { requestMethods: new Map(), pending: null, emittedSessionIds: new Set() },
  };
  const codexSubagentsRef: React.MutableRefObject<Map<string, CodexSubagentState>> = {
    current: new Map(),
  };
  const codexSubagentTerminalsRef: React.MutableRefObject<number> = { current: 0 };
  const acpEmittedSessionIds: React.MutableRefObject<Set<string>> = { current: new Set() };

  const setBlocks = (blocks: TurnBlock[]) => {
    blocksRef.current = blocks;
    listeners.onBlocks?.(blocks);
  };

  const hasPendingBackgroundTasks = (): boolean =>
    blocksRef.current.some(
      (b) => b.type === 'tool_call' && b.status === 'in_progress' && b.extra?.backgroundTaskId,
    );

  // The single funnel into `activeModel`, and so the single place the
  // "displayable model id" invariant holds — a `<synthetic>` placeholder from
  // any protocol reader stops here rather than reaching the chip or a turn
  // footer. See {@link ./active-model.ts}.
  const noteModel = (model: string | null) => {
    // Cheap identity check first: every assistant frame in a long transcript
    // re-notes the same model, and only the rare frame that actually changes
    // it is worth matching against the sentinel shape. A sentinel is never
    // stored, so it can never satisfy the equality check and slip through.
    if (!model || model === activeModelRef.current) return;
    if (isSentinelModelId(model)) return;
    activeModelRef.current = model;
    listeners.onActiveModel?.(model);
  };

  const acceptsSwitch = (sequence: number | null): boolean =>
    sequence === null || switchSequenceRef.current === null || sequence > switchSequenceRef.current;

  const noteSwitch = (change: RuntimeSwitch): boolean => {
    if (!acceptsSwitch(change.sequence)) return false;
    noteModel(change.model);
    if (change.sequence !== null) switchSequenceRef.current = change.sequence;
    return true;
  };

  const pushBlock = (block: TurnBlock) => {
    // Every protocol announces its session the same way — a `system_init`
    // block — so reading the working directory as those arrive keeps one place
    // that knows where the agent runs, whoever pushed the block.
    if (block.type === 'system_init') {
      const cwd = initWorkingDir(block);
      if (cwd) {
        workingDirRef.current = cwd;
        listeners.onWorkingDir?.(cwd);
      }
      // Session init announces the model the process starts on. Seed only
      // when nothing is known yet: the block can be emitted late (native
      // Codex holds it back until the first `turn/started`), by which point
      // a `/model` switch already in the stream is the newer truth.
      if (!activeModelRef.current) noteModel(block.model);
    }
    // Stamp the directory in effect right now, so the block keeps answering
    // "relative to where?" with the answer that was true when it was produced.
    // A later `cd` moves the session, not this row. See {@link BlockWorkingDir}.
    // Replay folds events in stream order, so a backfilled transcript stamps
    // exactly what the live one did.
    const stamped: TurnBlock = { ...block, workingDir: workingDirRef.current };
    setBlocks([...blocksRef.current, stamped]);
  };

  const updateBlocks = (updater: (blocks: TurnBlock[]) => TurnBlock[]) => {
    setBlocks(updater(blocksRef.current));
  };

  const lastBlock = (): TurnBlock | undefined => blocksRef.current[blocksRef.current.length - 1];

  const finalizeThinking = (eventTimestamp: number) => {
    if (!thinkingStartRef.current) return;
    const duration = durationSecs(thinkingStartRef.current, eventTimestamp, 0);
    setBlocks(
      blocksRef.current.map((b) =>
        b.type === 'thinking' && b.isActive ? { ...b, isActive: false, duration } : b,
      ),
    );
    thinkingStartRef.current = null;
  };

  const flushToMessage = (
    extra: Partial<Omit<ChatMessage, 'id' | 'role' | 'content' | 'blocks'>> | undefined,
    eventTimestamp: number,
  ): ChatItem | null => {
    if (thinkingStartRef.current) {
      const duration = durationSecs(thinkingStartRef.current, eventTimestamp, 0);
      blocksRef.current = blocksRef.current.map((b) =>
        b.type === 'thinking' && b.isActive ? { ...b, isActive: false, duration } : b,
      );
      thinkingStartRef.current = null;
    }

    const turnBlocks = blocksRef.current;

    if (turnBlocks.length === 0) return null;

    // If the only pending blocks are system_init, don't flush them into a standalone
    // assistant message — AssistantTurn filters system_init out of the renderable body
    // and uses it only as the popover behind the model badge, so flushing here would
    // produce an empty "ghost" card. Keep them pending so they attach to the next real
    // assistant turn instead.
    if (turnBlocks.every((b) => b.type === 'system_init')) return null;

    for (const block of turnBlocks) {
      if (block.type !== 'text' && block.type !== 'thinking') continue;
      const itemId = block.extra?.codexItemId;
      if (typeof itemId !== 'string' || !itemId) continue;
      const flushed = codexFlushedItemTextRef.current;
      flushed.set(itemId, (flushed.get(itemId) ?? '') + block.text);
    }

    setBlocks([]);

    return {
      id: finalizedAssistantId(turnBlocks, extra?.sourceEvent),
      role: 'assistant',
      content: '',
      blocks: turnBlocks,
      // Stamped per turn, not read from the agent record: a mid-session
      // `/model` switch must not relabel the turns the previous model
      // already answered.
      ...(activeModelRef.current ? { model: activeModelRef.current } : {}),
      ...extra,
    };
  };

  const clearLiveBlocks = () => {
    setBlocks([]);
  };

  const reset = () => {
    thinkingStartRef.current = null;
    planEntriesRef.current = [];
    streamingTodoInputRef.current = null;
    pendingResultRef.current = null;
    codexTokenUsageRef.current = null;
    codexFlushedItemTextRef.current = new Map();
    codexSessionInitRef.current = {
      requestMethods: new Map(),
      pending: null,
      emittedSessionIds: new Set(),
    };
    codexSubagentsRef.current = new Map();
    codexSubagentTerminalsRef.current = 0;
    acpEmittedSessionIds.current = new Set();
    activeModelRef.current = null;
    switchSequenceRef.current = null;
    setBlocks([]);
    workingDirRef.current = null;
    listeners.onWorkingDir?.(null);
    listeners.onActiveModel?.(null);
  };

  const seedSessionFacts = (facts: {
    workingDir: string | null;
    activeModel: string | null;
    switchSequence?: number | null;
  }) => {
    if (facts.workingDir && !workingDirRef.current) {
      workingDirRef.current = facts.workingDir;
      listeners.onWorkingDir?.(facts.workingDir);
    }
    if (!activeModelRef.current) noteModel(facts.activeModel);
    const seeded = facts.switchSequence ?? null;
    if (
      seeded !== null &&
      (switchSequenceRef.current === null || seeded > switchSequenceRef.current)
    ) {
      switchSequenceRef.current = seeded;
    }
  };

  const snapshot = () => ({
    currentTurnBlocks: blocksRef.current,
    workingDir: workingDirRef.current,
    activeModel: activeModelRef.current,
    switchSequence: switchSequenceRef.current,
  });

  return {
    get currentTurnBlocks() {
      return blocksRef.current;
    },
    get workingDir() {
      return workingDirRef.current;
    },
    get activeModel() {
      return activeModelRef.current;
    },
    snapshot,
    noteModel,
    acceptsSwitch,
    noteSwitch,
    pushBlock,
    updateBlocks,
    lastBlock,
    finalizeThinking,
    flushToMessage,
    clearLiveBlocks,
    reset,
    seedSessionFacts,
    blocksRef,
    thinkingStartRef,
    planEntriesRef,
    streamingTodoInputRef,
    pendingResultRef,
    codexTokenUsageRef,
    codexFlushedItemTextRef,
    codexSessionInitRef,
    codexSubagentsRef,
    codexSubagentTerminalsRef,
    acpEmittedSessionIds,
    hasPendingBackgroundTasks,
  };
}

/**
 * The live transcript's block manager: a {@link createBlockManager} core
 * whose reactive fields are mirrored into React state, so the view
 * re-renders as the buffer and the announced session facts change.
 */
export function useBlockManager(): BlockManager {
  const [currentTurnBlocks, setCurrentTurnBlocks] = useState<TurnBlock[]>([]);
  const [workingDir, setWorkingDir] = useState<string | null>(null);
  const [activeModel, setActiveModel] = useState<string | null>(null);
  const coreRef = useRef<BlockManagerCore | null>(null);
  if (coreRef.current === null) {
    coreRef.current = createBlockManager({
      onBlocks: setCurrentTurnBlocks,
      onWorkingDir: setWorkingDir,
      onActiveModel: setActiveModel,
    });
  }
  const core = coreRef.current;

  return useMemo<BlockManager>(
    () => ({
      ...core,
      currentTurnBlocks,
      workingDir,
      activeModel,
    }),
    [core, currentTurnBlocks, workingDir, activeModel],
  );
}
